import React, { useContext, useEffect, useState } from 'react';
import {
	Head,
	Wrapper,
	StartDatePicker,
	HintMessage,
	ContentWrapper,
	Modal,
	Button,
	Setback,
	Typography,
	Hint,
	IHelpContent,
	getHintContent,
	Richtext,
	Info,
	IconButton,
} from '@adac-mp/components';
import {
	IDates,
	VignetteContext,
	IOfferContext,
	OfferContext,
	getPriceAmountByCatalogId,
	getCorrespondingTransitionVignette,
	convertApiDate,
	formatDate,
	IVignetteOffer,
	ITypeVignetteOrderingProcessFields,
	getLabelValue,
} from '@adac-mp/shared';
import { format, addDays } from 'date-fns';
import { withTracking } from '@adac-mp/components/src/utils/tracking/tracking';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import { CustomInput } from '@adac-mp/components/src/utils/renderCustomInput';
import { DateSelectionDrawer } from '../one-cart/dateSelectionDrawer';

interface ITimeSelectionProps {
	hasError?: boolean;
	helpContent?: IHelpContent;
	supplierHasMultipleProducts?: boolean;
	inputDisabled?: boolean;
	handleCountryRemoved?: React.Dispatch<React.SetStateAction<boolean>>;
	updateStepCount?: React.Dispatch<React.SetStateAction<number>>;
	focusOnSectionsFirstElement?: (sectionStep?: number) => void;
	updateValidatedStepsMap?: (hasError: boolean, reset?: boolean) => void;
}

const renderTransitionHintMessage = (
	selectedVignette: IVignetteOffer,
	correspondingTransitionVignette: IVignetteOffer,
) => (
	<>
		<span className="flex justify-between">
			<Typography color="title" variant="title" size="lgEven">
				{selectedVignette.serviceCatalogName} {selectedVignette.servcatSeason}:
			</Typography>
			<Typography color="title" variant="title" size="lgEven">
				{getPriceAmountByCatalogId(selectedVignette.priceComponents)}€
			</Typography>
		</span>
		<Typography color="title" size="lgEven">
			{formatDate(convertApiDate(selectedVignette.usageFrom))}-{' '}
			{formatDate(convertApiDate(selectedVignette.usageTo))}
		</Typography>
		<span className="flex justify-between mt-3">
			<Typography color="title" variant="title" size="lgEven">
				{correspondingTransitionVignette.serviceCatalogName} {correspondingTransitionVignette.servcatSeason}:
			</Typography>
			<Typography color="title" variant="title" size="lgEven">
				{getPriceAmountByCatalogId(correspondingTransitionVignette.priceComponents)}€
			</Typography>
		</span>
		<Typography color="title" size="lgEven">
			{formatDate(convertApiDate(correspondingTransitionVignette.usageFrom))} -{' '}
			{formatDate(convertApiDate(correspondingTransitionVignette.usageTo))}
		</Typography>
	</>
);

const TimeSelectionComponent = ({
	hasError,
	helpContent,
	supplierHasMultipleProducts,
	inputDisabled,
	handleCountryRemoved = () => {},
	updateStepCount = () => {},
	focusOnSectionsFirstElement = () => {},
	updateValidatedStepsMap = () => {},
}: ITimeSelectionProps) => {
	const {
		selectedProducts,
		contentfulLabels,
		isTransitionPeriod,
		selectedCountries,
		removeCountryBySupplierId,
		removeSelectedProductsBySupplierId,
		updateSelectedProductsDates,
		removeSelectedProductsDates,
		updateSelectedProducts,
	} = useContext(VignetteContext);
	const { viewport } = useViewport();
	const currSelectedProduct = selectedProducts.find(({ moduleTypeId }) => moduleTypeId === 2);
	const isSchweizSelected = selectedCountries.find((selectedCountry) => selectedCountry.supplierId === 14);
	const isOnlySchweizSelected = isSchweizSelected && selectedCountries.length === 1;
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;
	const { vignetteOffers, labels } = useContext<IOfferContext>(OfferContext);
	const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
	const [sloModalIsOpen, setSloModalIsOpen] = useState<boolean>(false);
	const [dates, setDates] = useState<IDates | undefined>(
		isOnlySchweizSelected ? currSelectedProduct?.dates : undefined,
	);
	const [error, setError] = useState(false);
	const [drawerIsOpen, setDrawerIsOpen] = useState<boolean>(false);
	const correspondingTransitionVignette = getCorrespondingTransitionVignette(
		isTransitionPeriod,
		vignetteOffers,
		currSelectedProduct,
	);

	useEffect(() => {
		const currError = Boolean(currSelectedProduct?.dates?.start && dates);
		updateValidatedStepsMap(currError);
	}, [currSelectedProduct?.dates, dates]);

	const getValue = () => {
		if (!currSelectedProduct?.dates || !dates) {
			return '';
		}

		const startDate = format(currSelectedProduct.dates.start, 'dd.MM.yyyy');
		const endDate = format(currSelectedProduct.dates.end, 'dd.MM.yyyy');
		const hasMoreThanOneVignette = selectedProducts.filter(({ moduleTypeId }) => moduleTypeId === 2).length > 1;

		return startDate === endDate || hasMoreThanOneVignette ? startDate : `${startDate} – ${endDate}`;
	};

	const update = () => {
		if (dates && currSelectedProduct) {
			const slovenianProduct = selectedProducts.find(({ supplierId }) => supplierId === 7);
			if (slovenianProduct) {
				const maxSellUsageDay = addDays(new Date(), slovenianProduct.maxDaysSellUsage - 1).getTime();
				const startDate30DaysInAdvance = dates.start.getTime() >= maxSellUsageDay;
				if (startDate30DaysInAdvance) {
					setSloModalIsOpen(true);
					return;
				}
			}
			updateSelectedProductsDates(dates.start);
		}
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			setModalIsOpen(true);
		}
	};

	const renderSchweizHintMessage = () => {
		const hints: Hint[] = getHintContent('startDateSchweiz', [], currentLabels?.hintMessages);

		return <HintMessage className="mt-4 md:mt-2 flex flex-col gap-3" size={'small'} hints={hints} />;
	};

	const renderHintMessages = () => {
		if (isOnlySchweizSelected) {
			return renderSchweizHintMessage();
		}

		const hints: Hint[] = getHintContent(
			'startDate',
			[
				{
					tip: 'Hinweis:',
					message:
						'Sie können Ihre digitale Vignette vor Gültigkeitsbeginn kostenfrei stornieren oder das Kennzeichen und den Gültigkeitsbeginn kostenfrei ändern.',
				},
			],
			currentLabels?.hintMessages,
		);

		if (isTransitionPeriod && currSelectedProduct && correspondingTransitionVignette) {
			hints.unshift({
				message: renderTransitionHintMessage(currSelectedProduct, correspondingTransitionVignette),
			});
		}

		return (
			<>
				<HintMessage
					className="mt-4 md:mt-2 flex flex-col gap-3"
					size={isTransitionPeriod ? 'medium' : 'small'}
					hints={hints}
				/>
				{isSchweizSelected && renderSchweizHintMessage()}
			</>
		);
	};

	// eslint-disable-next-line react/display-name
	const renderButtonGroup = () => (className?: string) =>
		(
			<div className={`sticky rounded-b bottom-0 bg-white flex items-center justify-between p-3 ${className}`}>
				<Button
					variant="tertiary"
					size="sm"
					className="group gap-2 bg-white"
					onClick={() => {
						setDates(undefined);
						if (currSelectedProduct && currSelectedProduct.dates) {
							removeSelectedProductsDates();
							updateValidatedStepsMap(true);
						}
					}}>
					<Setback className="group-hover:[&>*]:stroke-gray-100" size="20" />
					{getLabelValue('mp.reset', labels) || 'Zurücksetzen'}
				</Button>
				<Button
					className="px-3 md:px-4"
					data-cy="finish-button"
					onClick={() => {
						if (!dates && !currSelectedProduct?.dates) {
							setError(true);
							return;
						}
						setError(false);
						setModalIsOpen(false);
						update();
						focusOnSectionsFirstElement();
					}}>
					{getLabelValue('mp.done', labels) || 'Fertig'}
				</Button>
			</div>
		);

	const closeSloOverlay = () => {
		setSloModalIsOpen(false);
		setModalIsOpen(true);
	};

	const renderDateSelectionHelpDrawer = () => {
		return <DateSelectionDrawer id="dateSelection" drawerIsOpen={drawerIsOpen} setDrawerIsOpen={setDrawerIsOpen} />;
	};

	const renderHeadline = () => {
		if (helpContent) {
			return (
				<Head
					headline={currentLabels?.headlineStartDate || 'Ab wann benötigen Sie die Vignette?'}
					description={
						currSelectedProduct?.supplierId === 7 &&
						(currentLabels?.descriptionStartDate ||
							'Sie können die sofortige Gültigkeit wählen oder einen beliebigen Tag innerhalb der kommenden 30 Tagen als Gültigkeitsbeginn festlegen.')
					}
					helpContent={helpContent}
					trackingElement="Gültigkeit"
				/>
			);
		}

		return (
			<Head
				headline={currentLabels?.headlineStartDate || 'Ab wann benötigen Sie die Vignette?'}
				description={
					currSelectedProduct?.supplierId === 7 &&
					(currentLabels?.descriptionStartDate ||
						'Sie können die sofortige Gültigkeit wählen oder einen beliebigen Tag innerhalb der kommenden 30 Tagen als Gültigkeitsbeginn festlegen.')
				}
				trackingElement="Gültigkeit">
				<IconButton
					title={currentLabels?.headlineStartDate || 'Ab wann benötigen Sie die Vignette?'}
					ariaLabel={`Info Button ${
						currentLabels?.headlineStartDate || 'Ab wann benötigen Sie die Vignette?'
					}`}
					className={`opacity-80 border-none transition-opacity hover:opacity-100 rounded-half focus-visible:outline outline-2 absolute bottom-[3px] ml-1.5 md:bottom-[9px]`}
					tracking={{
						icon_click: {
							label: `Infos zur ${
								currentLabels?.headlineStartDate || 'Ab wann benötigen Sie die Vignette?'
							}`,
							element: currentLabels?.headlineStartDate || 'Ab wann benötigen Sie die Vignette?',
							category: 'Content',
						},
					}}
					onClick={(e: any) => {
						e.stopPropagation();
						setDrawerIsOpen(true);
					}}>
					<Info strokeWidth="2" title={'iconTitle'} size={viewport === 'desktop' ? '32' : '24'} />
				</IconButton>
			</Head>
		);
	};

	return (
		<>
			<Wrapper
				animate
				className="bg-transparent"
				paddingTop={`${supplierHasMultipleProducts ? 'large' : 'small'}`}>
				{renderHeadline()}
				<ContentWrapper>
					<CustomInput
						value={getValue()}
						hasError={hasError || error}
						disabled={Boolean(inputDisabled)}
						placeholder={currentLabels?.placeholderStartDate || 'Startdatum'}
						helperText={currentLabels?.errorStartDate || 'Bitte wählen Sie ein Startdatum'}
						onClick={() => {
							setModalIsOpen(true);
						}}
						onKeyDown={onKeyDown}
					/>
					{renderHintMessages()}
				</ContentWrapper>
			</Wrapper>
			<Modal
				noHeadline
				fit
				isOpen={modalIsOpen}
				buttonGroup={renderButtonGroup()}
				onClose={() => {
					setDates(undefined);
					setModalIsOpen(false);
				}}>
				<StartDatePicker
					inline
					customInput={CustomInput}
					dates={dates}
					hasError={hasError || error}
					onStartDateChange={(vignette, newDates) => {
						updateSelectedProducts(vignette as IVignetteOffer, true);
						setDates(newDates);
					}}
				/>
			</Modal>
			<Modal
				withSubmissionButtons
				title={currentLabels?.validationModal?.titleDuplicate || 'Achtung'}
				isOpen={sloModalIsOpen}
				submitButtonLabel={currentLabels?.validationModal?.oneCartSlovenianDateSubmit || 'Datum ändern'}
				cancelButtonLabel={
					currentLabels?.validationModal?.oneCartSlovenianDateCancel || 'Ohne slowenische Vignette fortfahren'
				}
				onSubmit={closeSloOverlay}
				onCancel={() => {
					removeSelectedProductsBySupplierId(7);
					removeCountryBySupplierId(7);
					updateStepCount((prev) => prev - 1);
					handleCountryRemoved(true);
					if (dates && currSelectedProduct) {
						updateSelectedProductsDates(dates.start);
					}
					setSloModalIsOpen(false);
				}}
				onClose={closeSloOverlay}>
				<Typography component="p" className="px-3 pt-1.5 pb-3" size="lg">
					{currentLabels?.validationModal?.oneCartSlovenianDateText ? (
						<Richtext document={currentLabels?.validationModal.oneCartSlovenianDateText} />
					) : (
						<span>
							Sie haben ein Startdatum gewählt, das mehr als 30 Tage in der Zukunft liegt. Dadurch ist die
							Slowenische Vignette noch nicht verfügbar. <br /> <br /> Wenn Sie sich entscheiden, ohne die
							slowenische Vignette fortzufahren, erinnern wir Sie per E-Mail, sobald diese für Ihr
							gewähltes Startdatum verfügbar ist
						</span>
					)}
				</Typography>
			</Modal>
			{renderDateSelectionHelpDrawer()}
		</>
	);
};

export const TimeSelection = withTracking(TimeSelectionComponent);
